import authService from './auth.service';

export interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
}

/**
 * API interceptor to automatically add authorization headers
 */
export async function apiRequest(url: string, config: RequestConfig = {}): Promise<Response> {
  const { skipAuth = false, headers = {}, ...restConfig } = config;
  
  // Get auth token
  const token = authService.getToken();
  
  // Create headers object
  const requestHeaders: Record<string, string> = {};
  
  // Copy existing headers
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      requestHeaders[key] = value;
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      requestHeaders[key] = value;
    });
  } else if (headers) {
    Object.assign(requestHeaders, headers);
  }
  
  // Add authorization header if token exists and not skipped
  if (token && !skipAuth) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  // Make the request
  const response = await fetch(url, {
    ...restConfig,
    headers: requestHeaders
  });
  
  // Handle 401 Unauthorized
  if (response.status === 401 && !skipAuth) {
    // Don't redirect for API calls, just clear the token
    authService.logout();
    // Don't redirect - let the calling code handle it
  }
  
  return response;
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (url: string, config?: RequestConfig) => 
    apiRequest(url, { ...config, method: 'GET' }),
    
  post: (url: string, body?: any, config?: RequestConfig) => 
    apiRequest(url, { 
      ...config, 
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: body instanceof FormData ? config?.headers : {
        'Content-Type': 'application/json',
        ...config?.headers
      }
    }),
    
  put: (url: string, body?: any, config?: RequestConfig) => 
    apiRequest(url, { 
      ...config, 
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: body instanceof FormData ? config?.headers : {
        'Content-Type': 'application/json',
        ...config?.headers
      }
    }),
    
  delete: (url: string, config?: RequestConfig) => 
    apiRequest(url, { ...config, method: 'DELETE' }),
    
  patch: (url: string, body?: any, config?: RequestConfig) => 
    apiRequest(url, { 
      ...config, 
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: body instanceof FormData ? config?.headers : {
        'Content-Type': 'application/json',
        ...config?.headers
      }
    })
};

export default api;